<template>
    <div>
        <p>
            To prevent spam, the major cell phone carriers require that you register your organization with The Campaign
            Registry (TCR). RedChirp handles this for you using the information you provide below. <strong>We recommend
                completing this step as soon as possible to avoid delays in launching your texting program.</strong>
        </p>

        <v-stepper
            v-model="step"
            flat
            vertical
        >
            <v-stepper-step
                step="1"
                :rules="rules_org_info"
                :complete="org_info_section_complete"
                complete-icon="mdi-check"
                edit-icon="mdi-check"
                editable
            >Organization Info</v-stepper-step>

            <v-stepper-content step="1">
                <v-row>
                    <v-col
                        cols="12"
                        md="8"
                        class="shaded-light py-6 px-8 px-md-10"
                    >
                        <m-text-validate-field
                            ref="legal_name"
                            label="Legal Company Name"
                            v-model="tcr_setup_data.organization_legal_name"
                            hint="This should match *exactly* what the IRS has on file for your company's EIN."
                            persistent-hint
                            @input="update"
                            required
                        />

                        <v-checkbox
                            class="mb-0"
                            label="My business has a different DBA (Doing Business As)"
                            v-model="tcr_setup_data.has_dba"
                            :class="[!tcr_setup_data.has_dba && 'mb-4']"
                            hide-details
                            @change="doUpdate"
                        />
                        <m-text-validate-field
                            v-if="tcr_setup_data.has_dba"
                            ref="dba_name"
                            label="DBA"
                            v-model="tcr_setup_data.dba"
                            autocomplete="off"
                            @input="update"
                            hint="DBA or Brand Name, if different from legal name"
                            persistent-hint
                            class="my-4"
                            required
                        />

                        <v-select
                            class="mb-6"
                            required
                            v-model="tcr_setup_data.tcr_legal_form"
                            :items="$rc.enumChoices('tcr_organiztion_legal_form', false)"
                            hint="Choose which best describes your organization. Most RedChirp customers choose 'Private Company'"
                            persistent-hint
                            @change="doUpdate"
                        >
                            <template #label>
                                <span class="text-body-2">
                                    Legal Form <span class="error--text">*</span>
                                </span>
                            </template>
                        </v-select>

                        <m-text-validate-field
                            ref="tax_id"
                            label="Tax Identification Number (EIN)"
                            class="mb-2"
                            v-model="tcr_setup_data.tax_id"
                            @input="update"
                            us_or_ca_tax_id
                            required
                        />

                        <div class="d-flex justify-end align-center mb-2">
                            <v-tooltip bottom>
                                <template #activator="{ on, attrs }">
                                    <v-btn
                                        text
                                        small
                                        color="primary"
                                        class="text-decoration-underline text-uppercase mt-1 pa-0"
                                        v-on="on"
                                        v-bind="attrs"
                                    >
                                        <strong>What's My Tax Identification Number?</strong>
                                    </v-btn>
                                </template>

                                <div>
                                    <p class="mb-0"><strong>US:</strong>
                                        Use your 9-digit Employer Identification Number (EIN)
                                    </p>
                                    <p class="mb-0">
                                        <strong>Canada:</strong> Nine Digit Business Number (BN 9 - not BN15)
                                    </p>
                                </div>
                            </v-tooltip>
                        </div>
                    </v-col>

                    <v-col cols="12">
                        <v-btn
                            color="primary"
                            class="mt-1 mb-4"
                            @click="step++"
                        >
                            Next
                        </v-btn>
                    </v-col>
                </v-row>
            </v-stepper-content>

            <v-stepper-step
                step="2"
                :rules="rules_business_address"
                :complete="business_address_section_complete"
                complete-icon="mdi-check"
                edit-icon="mdi-check"
                editable
            >Business Address</v-stepper-step>

            <v-stepper-content step="2">
                <v-row>
                    <v-col
                        cols="12"
                        md="8"
                        class="shaded-light pt-8 pb-4 px-8 px-md-10"
                    >
                        <v-row>
                            <v-col cols="12">
                                <m-text-validate-field
                                    label="Business Address"
                                    v-model="tcr_setup_data.business_address"
                                    @input="update"
                                    hint="This should match exactly what's on file with the IRS and/or your state/province's business registration office"
                                    persistent-hint
                                    required
                                />
                            </v-col>
                            <v-col
                                cols="12"
                                md="6"
                            >
                                <m-text-validate-field
                                    label="City"
                                    v-model="tcr_setup_data.business_address_city"
                                    @input="update"
                                    required
                                />
                            </v-col>
                            <v-col
                                cols="12"
                                md="6"
                            >
                                <m-text-validate-field
                                    :label="state_or_province"
                                    v-model="tcr_setup_data.business_address_state"
                                    @input="update"
                                    required
                                />
                            </v-col>
                            <v-col
                                cols="12"
                                md="6"
                            >
                                <m-text-validate-field
                                    label="Zip / Postal Code"
                                    v-model="tcr_setup_data.business_address_zip"
                                    @input="update"
                                    required
                                />
                            </v-col>
                            <v-col
                                cols="12"
                                md="6"
                            >
                                <v-select
                                    label="Country"
                                    v-model="tcr_setup_data.country"
                                    :items="$rc.enumChoices('tcr_country', false)"
                                    @change="doUpdate"
                                />
                            </v-col>
                        </v-row>
                    </v-col>

                    <v-col cols="12">
                        <v-row
                            class="mt-1 mb-4"
                            dense
                        >
                            <v-col cols="auto">
                                <v-btn
                                    color="secondary"
                                    outlined
                                    @click="step--"
                                >Previous</v-btn>
                            </v-col>

                            <v-col cols="auto">
                                <v-btn
                                    color="primary"
                                    @click="step++"
                                >Next</v-btn>
                            </v-col>
                        </v-row>
                    </v-col>
                </v-row>
            </v-stepper-content>

            <v-stepper-step
                step="3"
                :rules="rules_business_details"
                :complete="business_details_section_complete"
                complete-icon="mdi-check"
                edit-icon="mdi-check"
                editable
            >Business Details</v-stepper-step>

            <v-stepper-content step="3">
                <v-row>
                    <v-col
                        cols="12"
                        md="8"
                        class="shaded-light pt-8 pb-4 px-8 px-md-10"
                    >

                        <v-select
                            v-model="tcr_setup_data.tcr_business_category"
                            :items="biz_category_choices"
                            required
                            @change="doUpdate"
                        >
                            <template #label>
                                <span class="text-body-2">
                                    Which best describes your business vertical? (pick one) <span
                                        class="error--text">*</span>
                                </span>
                            </template>
                        </v-select>

                        <m-text-validate-field
                            label="Website"
                            class="mb-6"
                            v-model="tcr_setup_data.website"
                            autocomplete="off"
                            @input="update"
                            @blur="onUrlBlur('website')"
                            hint="Your organization's primary website (e.g. for Microsoft, https://www.microsoft.com)"
                            persistent-hint
                            required
                            url
                        />
                    </v-col>

                    <v-col cols="12">
                        <v-row
                            class="mt-1 mb-4"
                            dense
                        >
                            <v-col cols="auto">
                                <v-btn
                                    color="secondary"
                                    outlined
                                    @click="step--"
                                >
                                    Previous
                                </v-btn>
                            </v-col>

                            <v-col cols="auto">
                                <v-btn
                                    color="primary"
                                    @click="step++"
                                >
                                    Next
                                </v-btn>
                            </v-col>
                        </v-row>
                    </v-col>
                </v-row>
            </v-stepper-content>

            <v-stepper-step
                step="4"
                :rules="rules_contact_info"
                :complete="contact_info_section_complete"
                complete-icon="mdi-check"
                edit-icon="mdi-check"
                editable
            >Contact Info</v-stepper-step>

            <v-stepper-content step="4">
                <v-row>
                    <v-col
                        cols="12"
                        md="8"
                        class="shaded-light pt-6 pb-7 px-8 px-md-10"
                    >
                        <m-text-validate-field
                            label="Email Address - TCR Point of Contact"
                            class="mb-6"
                            v-model="tcr_setup_data.tcr_contact_email"
                            @input="update"
                            hint="The Campaign Registry requires you to provide an official email address (not a personal one) for your organization's primary, individual contact. This email address is used in your default response to the HELP CTIA keyword."
                            persistent-hint
                            required
                            official_email
                        />
                        <m-text-validate-field
                            label="Phone Number - TCR Point of Contact "
                            v-model="tcr_setup_data.tcr_phone_number"
                            @input="update"
                            hint="The Campaign Registry requires you to provide an official phone number (not a personal one) for your organization's primary, individual contact."
                            persistent-hint
                            required
                            phone
                        />
                    </v-col>

                    <v-col cols="12">
                        <v-row
                            class="mt-1 mb-4"
                            dense
                        >
                            <v-col cols="auto">
                                <v-btn
                                    color="secondary"
                                    outlined
                                    @click="step--"
                                >
                                    Previous
                                </v-btn>
                            </v-col>

                            <v-col cols="auto">
                                <v-btn
                                    color="primary"
                                    @click="step++"
                                >
                                    Next
                                </v-btn>
                            </v-col>
                        </v-row>
                    </v-col>
                </v-row>
            </v-stepper-content>

            <v-stepper-step
                step="5"
                :rules="rules_policies"
                :complete="policies_section_complete"
                complete-icon="mdi-check"
                edit-icon="mdi-check"
                editable
            >
                Policies
            </v-stepper-step>

            <v-stepper-content step="5">
                <v-row>
                    <v-col
                        cols="12"
                        md="12"
                        xl="7"
                        class="shaded-light pt-5 pb-6 px-8 px-md-10"
                    >
                        <div class="mb-2">
                            <h3 class="text-h6 my-2">Privacy Policy</h3>

                            <m-text-validate-field
                                label="Privacy Policy URL"
                                class="mb-4"
                                v-model="tcr_setup_data.url_privacy_policy"
                                autocomplete="off"
                                @input="update"
                                @blur="onUrlBlur('url_privacy_policy')"
                                hint="The web address (URL) for your organization's privacy policy (e.g. for Microsoft, https://privacy.microsoft.com/en-us/privacy-statement)"
                                persistent-hint
                                required
                                url
                            />

                            <v-select
                                v-model="tcr_setup_data.privacy_policy_addendum"
                                :items="privacy_policy_addendum_options"
                                @change="update"
                                required
                            >
                                <template #label>
                                    <span class="text-body-2">
                                        Privacy Policy Addendum <span class="error--text">*</span>
                                    </span>
                                </template>
                            </v-select>
                        </div>

                        <div>
                            <h3 class="text-h6 my-2">Terms and Conditions</h3>

                            <m-text-validate-field
                                label="Terms and Conditions URL"
                                class="mb-4"
                                v-model="tcr_setup_data.url_terms_and_conditions"
                                autocomplete="off"
                                @input="update"
                                @blur="onUrlBlur('url_terms_and_conditions')"
                                hint="The web address (URL) for your organization's terms and conditions (e.g. for Microsoft, https://www.microsoft.com/en-us/legal/terms-of-use)"
                                persistent-hint
                                required
                                url
                            />

                            <v-select
                                v-model="tcr_setup_data.terms_and_conditions_addendum"
                                :items="terms_and_conditions_addendum_options"
                                @change="update"
                                required
                            >
                                <template #label>
                                    <span class="text-body-2">
                                        Text Messaging Terms and Conditions Addendum <span class="error--text">*</span>
                                    </span>
                                </template>
                            </v-select>
                        </div>
                    </v-col>

                    <v-col
                        cols="12"
                        md="auto"
                        xl="5"
                        class="pt-3 pb-6 px-4"
                    >
                        <v-card
                            outlined
                            flat
                            class="pa-4"
                        >
                            <h4 class="text-subtitle-1 font-weight-bold mb-2">Policy Resources and Templates</h4>

                            <div class="d-flex flex-column align-start">
                                <rc-link
                                    :to="{
                                        name: 'default-privacy-policy-addendum',
                                        params: {
                                            organization_id: this.profile.organization_id,
                                        }
                                    }"
                                    text="Privacy Policy Addendum Template"
                                />
                                <p class="text-body-2 ma-0 mb-3">
                                    Our privacy policy addendum template, tailored for your organization.
                                </p>

                                <rc-link
                                    href="https://redchirp.com/texting-requirements-of-your-privacy-policy/"
                                    text="Privacy Policy Carrier Requirements"
                                />
                                <p class="text-body-2 ma-0 mb-3">
                                    Includes examples of acceptable privacy policy language for crafting your own.
                                </p>

                                <rc-link
                                    :to="{
                                        name: 'default-terms-and-conditions-addendum',
                                        params: {
                                            organization_id: this.profile.organization_id,
                                        }
                                    }"
                                    text="RedChirp terms and conditions addendum"
                                />
                                <p class="text-body-2 ma-0 mb-3">
                                    Our terms and conditions addendum template, tailored for your organization.
                                </p>

                                <rc-link
                                    href="https://redchirp.com/texting-requirements-and-your-websites-terms-and-conditions/"
                                    text="Terms & Conditions Carrier Requirements"
                                />
                                <p class="text-body-2 ma-0">
                                    Includes examples of acceptable terms and conditions language for crafting your own.
                                </p>
                            </div>
                        </v-card>
                    </v-col>

                    <v-col cols="12">
                        <v-row
                            class="mt-1 mb-4"
                            dense
                        >
                            <v-col cols="auto">
                                <v-btn
                                    color="secondary"
                                    outlined
                                    @click="step--"
                                >
                                    Previous
                                </v-btn>
                            </v-col>
                        </v-row>
                    </v-col>
                </v-row>
            </v-stepper-content>
        </v-stepper>
    </div>
</template>

<script>
import _ from 'lodash';
import {
    isValidTaxId,
    isValidOfficialEmail,
    isUSPhoneNumber
} from '../../js/validate.js';

export default {
    name: 'WelcomeTcr',

    props: {
        profile: {
            type: Object,
            default: function () { return {}; },
        },
    },

    data() {
        return {
            step: 1,
            tcr_setup_data: {},
            touched_steps: {},
            biz_category_choices: [
                "Agriculture",
                "Media and Communication",
                "Construction, Materials, and Trade Services",
                "Education",
                "Energy and Utilities",
                "Entertainment",
                "Financial Services",
                "Gambling and Lottery",
                "Government Services and Agencies",
                "Healthcare and Life Sciences",
                "Hospitality and Travel",
                "HR, Staffing or Recruitment",
                "Insurance",
                "Legal",
                "Manufacturing",
                "Non-profit Organization",
                "Political",
                "Postal and Delivery",
                "Professional Services",
                "Real Estate",
                "Retail and Consumer Products",
                "Information Technology Services",
                "Transportation or Logistics",
            ],
            terms_and_conditions_addendum_options: [
                { 'text': 'Adopt and use the RedChirp text messaging terms and conditions addendum to satisfy the carrier\'s vetting requirements', 'value': 'use-form-addendum' },
                { 'text': 'We have updated our website\'s terms and conditions to satisfy the carrier\'s vetting requirements (may require updating as new requirements are released)', 'value': 'do-not-use-form-addendum' },
            ],
            privacy_policy_addendum_options: [
                { 'text': 'Adopt and use the RedChirp text messaging privacy policy addendum to satisfy the carrier\'s vetting requirements', 'value': 'use-form-addendum' },
                { 'text': 'We have updated our website\'s privacy policy to satisfy the carrier\'s vetting requirements (may require updating as new requirements are released)', 'value': 'do-not-use-form-addendum' },
            ],
        }
    },

    computed: {
        state_or_province() {
            return this.tcr_setup_data.country === 'us' ? 'State' : 'Province';
        },

        org_info_section_complete() {
            return (
                !!this.tcr_setup_data.organization_legal_name &&
                (!this.tcr_setup_data.has_dba || !!this.tcr_setup_data.dba) &&
                (!!this.tcr_setup_data.tax_id && isValidTaxId(this.tcr_setup_data.tax_id))
            );
        },

        business_address_section_complete() {
            return (
                !!this.tcr_setup_data.business_address &&
                !!this.tcr_setup_data.business_address_city &&
                !!this.tcr_setup_data.business_address_state &&
                !!this.tcr_setup_data.business_address_zip &&
                !!this.tcr_setup_data.country
            );
        },

        business_details_section_complete() {
            return (
                !!this.tcr_setup_data.website &&
                (this.tcr_setup_data.website.includes('https://') || this.tcr_setup_data.website.includes('http://')) &&
                !!this.tcr_setup_data.tcr_business_category
            );
        },

        contact_info_section_complete() {
            return (
                !!this.tcr_setup_data.tcr_contact_email &&
                isValidOfficialEmail(this.tcr_setup_data.tcr_contact_email) &&
                !!this.tcr_setup_data.tcr_phone_number &&
                isUSPhoneNumber(this.tcr_setup_data.tcr_phone_number)
            );
        },

        policies_section_complete() {
            return (
                !!this.tcr_setup_data.url_privacy_policy &&
                !!this.tcr_setup_data.privacy_policy_addendum &&
                !!this.tcr_setup_data.url_terms_and_conditions &&
                !!this.tcr_setup_data.terms_and_conditions_addendum
            );
        },

        first_incomplete_section() {
            if (!this.org_info_section_complete) {
                return 1;
            }
            if (!this.business_address_section_complete) {
                return 2;
            }
            if (!this.business_details_section_complete) {
                return 3;
            }
            if (!this.contact_info_section_complete) {
                return 4;
            }
            if (!this.policies_section_complete) {
                return 5;
            }
            return null;
        },

        rules_org_info() {
            return this.touched_steps[1] ? [
                () => !!this.tcr_setup_data.organization_legal_name || 'Legal company name is required',
                () => (!this.tcr_setup_data.has_dba || !!this.tcr_setup_data.dba) || 'DBA is required',
                () => (!!this.tcr_setup_data.tax_id && isValidTaxId(this.tcr_setup_data.tax_id)) || 'Valid Tax ID is required',
            ] : [];
        },

        rules_business_address() {
            return this.touched_steps[2] ? [
                () => !!this.tcr_setup_data.business_address || 'Business address is required',
                () => !!this.tcr_setup_data.business_address_city || 'City is required',
                () => !!this.tcr_setup_data.business_address_state || `${this.state_or_province ?? 'State'} is required`,
                () => !!this.tcr_setup_data.business_address_zip || 'Zip/Postal code is required',
                () => !!this.tcr_setup_data.country || 'Country is required',
            ] : [];
        },

        rules_business_details() {
            return this.touched_steps[3] ? [
                () => (!!this.tcr_setup_data.website && this.tcr_setup_data.website?.includes('https://')) || 'Website is required',
            ] : [];
        },

        rules_contact_info() {
            return this.touched_steps[4] ? [
                () => (!!this.tcr_setup_data.tcr_contact_email && isValidOfficialEmail(this.tcr_setup_data.tcr_contact_email)) || 'TCR Point of Contact Email Address is required',
                () => (!!this.tcr_setup_data.tcr_phone_number && isUSPhoneNumber(this.tcr_setup_data.tcr_phone_number)) || 'TCR Point of Contact Phone Number is required',
            ] : [];
        },

        rules_policies() {
            return this.touched_steps[5] ? [
                () => !!this.tcr_setup_data.url_privacy_policy || 'Privacy Policy URL is required',
                () => !!this.tcr_setup_data.privacy_policy_addendum || 'Privacy Policy Addendum is required',
                () => !!this.tcr_setup_data.url_terms_and_conditions || 'Terms and Conditions URL is required',
                () => !!this.tcr_setup_data.terms_and_conditions_addendum || 'Terms and Conditions Addendum is required',
            ] : [];
        },
    },

    mounted() {
        this.tcr_setup_data = _.cloneDeep(this.profile.tcr_setup_data);

        if (this.tcr_setup_data.country == null)
            this.tcr_setup_data.country = 'us';

        this.step = this.first_incomplete_section ?? 1;
    },

    watch: {
        step(newStep, oldStep) {
            if (oldStep) {
                this.markStepAsTouched(oldStep);
            }
        }
    },

    methods: {
        markStepAsTouched(stepNumber) {
            this.$set(this.touched_steps, stepNumber, true);
        },

        update: _.debounce(function () {
            this.doUpdate();
        }, 751),

        doUpdate() {
            this.$rc.rest({
                spinner: false,
                endpoint: 'PUT: /api/v1/welcome/tcr-organization-setup-data',
                params: this.tcr_setup_data,
                on_success: (data) => {
                    if (data.complete == true) {
                        this.$emit('reload');
                    }
                },
                on_error: 'Unable to save form data',
            });
        },

        onUrlBlur(field) {
            const val = this.tcr_setup_data[field];
            this.tcr_setup_data[field] = this.$rc.maskUrl(val);
            this.update();
        },
    }
}
</script>

<style lang="scss" scoped>
.v-alert.hide-icon {
    :deep(.v-alert__icon) {
        display: none;
    }
}

:deep(.v-stepper__step--complete:not(.v-stepper__step--error) .v-stepper__step__step) {
    background-color: var(--v-success-base) !important;
}
</style>